import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import type { ModelViewerElement } from '@google/model-viewer';
import { ANNOTATION_DEFAULTS } from '@/utils/annotationConstants';
import { captureViewerCameraState, pickAnnotationSurfaceAtClientPoint } from '@/utils/annotationUtils';

type OnAnnotationAdd = (
  position: string,
  normal: string,
  orbit: string,
  target: string,
  cameraFieldOfView: string
) => void;

interface UseAnnotationPlacementOptions {
  modelViewerRef: RefObject<ModelViewerElement>;
  isAnnotationMode: boolean;
  modelUrl: string;
  onAnnotationAdd?: OnAnnotationAdd;
}

/**
 * Annotation placement mode: disable tap-to-recenter and handle click-to-place on the model surface.
 */
export function useAnnotationPlacement({
  modelViewerRef,
  isAnnotationMode,
  modelUrl,
  onAnnotationAdd,
}: UseAnnotationPlacementOptions) {
  const { t } = useTranslation();
  const onAnnotationAddRef = useRef(onAnnotationAdd);

  useEffect(() => {
    onAnnotationAddRef.current = onAnnotationAdd;
  }, [onAnnotationAdd]);

  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv) return;
    if (isAnnotationMode) {
      mv.setAttribute('disable-tap', '');
    } else {
      mv.removeAttribute('disable-tap');
    }
  }, [isAnnotationMode, modelViewerRef]);

  useEffect(() => {
    if (!isAnnotationMode || !modelUrl || !onAnnotationAddRef.current) return;

    let disposed = false;
    let placementGeneration = 0;
    let detachListeners: (() => void) | undefined;
    const DRAG_THRESHOLD = ANNOTATION_DEFAULTS.DRAG_THRESHOLD;

    const attachToViewer = (mv: ModelViewerElement) => {
      let mouseDownX = 0;
      let mouseDownY = 0;
      let mouseDownActive = false;
      let isDragging = false;
      const resetMouseState = () => {
        mouseDownX = 0;
        mouseDownY = 0;
        mouseDownActive = false;
        isDragging = false;
      };

      const handleMouseDown = (event: MouseEvent) => {
        if (event.button !== 0) return;
        mouseDownX = event.clientX;
        mouseDownY = event.clientY;
        mouseDownActive = true;
        isDragging = false;
      };

      const handleMouseMove = (event: MouseEvent) => {
        if (!mouseDownActive) return;

        const deltaX = Math.abs(event.clientX - mouseDownX);
        const deltaY = Math.abs(event.clientY - mouseDownY);

        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          isDragging = true;
        }
      };

      const handleMouseUp = (event: MouseEvent) => {
        if (event.button !== 0 || !mouseDownActive) return;

        const wasDragging = isDragging;
        const clickClientX = mouseDownX;
        const clickClientY = mouseDownY;
        const generation = ++placementGeneration;

        resetMouseState();

        if (wasDragging) return;

        void (async () => {
          try {
            const currentMv = modelViewerRef.current;
            if (!currentMv || disposed || generation !== placementGeneration) return;

            const surfacePick = await pickAnnotationSurfaceAtClientPoint(
              currentMv,
              clickClientX,
              clickClientY
            );

            if (disposed || generation !== placementGeneration) return;

            if (!surfacePick) {
              notifications.show({
                title: t('editProduct.error'),
                message: t('editProduct.annotationPositionInvalid'),
                color: 'orange',
              });
              return;
            }

            const cam = captureViewerCameraState(currentMv);

            onAnnotationAddRef.current?.(
              surfacePick.position,
              surfacePick.normal,
              cam.cameraOrbit,
              cam.cameraTarget,
              cam.cameraFieldOfView
            );
          } catch (error) {
            console.error('Error placing annotation:', error);
            if (disposed || generation !== placementGeneration) return;
            notifications.show({
              title: t('editProduct.error'),
              message: t('editProduct.annotationPositionError'),
              color: 'red',
            });
          }
        })();
      };

      mv.addEventListener('mousedown', handleMouseDown);
      mv.addEventListener('mousemove', handleMouseMove);
      mv.addEventListener('mouseup', handleMouseUp);
      mv.style.cursor = 'crosshair';

      return () => {
        placementGeneration += 1;
        mv.removeEventListener('mousedown', handleMouseDown);
        mv.removeEventListener('mousemove', handleMouseMove);
        mv.removeEventListener('mouseup', handleMouseUp);
        mv.style.cursor = '';
      };
    };

    const tryAttach = () => {
      if (disposed) return true;
      const mv = modelViewerRef.current;
      if (!mv) return false;
      detachListeners = attachToViewer(mv);
      return true;
    };

    if (!tryAttach()) {
      let attempts = 0;
      const retryAttach = () => {
        if (disposed || tryAttach() || attempts >= 30) return;
        attempts += 1;
        requestAnimationFrame(retryAttach);
      };
      requestAnimationFrame(retryAttach);
    }

    return () => {
      disposed = true;
      placementGeneration += 1;
      detachListeners?.();
    };
  }, [isAnnotationMode, modelUrl, modelViewerRef, t]);
}
